/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module WafCrsListPageModule
 */

/* eslint-disable max-classes-per-file */

import {
    AfterViewInit,
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    Inject,
    NgModule,
    NO_ERRORS_SCHEMA,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
} from '@angular/core';

import { L10nService, VIPModule } from '@vmw/ngx-vip';
import { AviCardModule } from 'nsx-alb-ui-components';
import { ICRSDetails } from 'generated-types';

import { SharedModule } from 'ng/shared/shared.module';
import { DataGridModule } from 'ng/modules/data-grid/data-grid.module';

import {
    IAviCollectionDataGridConfig,
} from 'ng/modules/data-grid/components/avi-collection-data-grid/avi-collection-data-grid.types';

import { LoadingState } from 'ng/shared/components/async-file-upload/async-file-upload.component';
import { NotificationService } from 'ng/modules/core/services/notification.service';

import * as globalL10n from 'global-l10n';

import {
    TWafCrsCollection,
    WafCrsCollection,
} from 'collections/waf-crs.collection.factory';

import type { WafCrsItem as WafCrs } from 'items/waf-crs.item.factory';

import {
    LatestWafCrsVersionService,
} from 'ajs/modules/waf/services/latest-waf-crs-version.service';

import * as l10n from './waf-crs-list-page.l10n';
import './waf-crs-list-page.component.less';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;
const { ...globalL10nKeys } = globalL10n;

/**
 * @description WAF CRS list page. Also includes functionality to upload CRS JSON files.
 * @author alextsg
 */
@Component({
    selector: 'waf-crs-list-page',
    templateUrl: './waf-crs-list-page.component.html',
})
export class WafCrsListPageComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('expandedContentTemplateRef')
    private readonly expandedContentTemplateRef: TemplateRef<HTMLElement>;

    /**
     * Keys from source bundles for template usage.
     */
    public readonly l10nKeys = l10nKeys;

    /**
     * Keys from global source bundles for template usage.
     */
    public readonly globalL10nKeys = globalL10nKeys;

    /**
     * Configuration object to display WAF CRS grid.
     */
    public wafCrsGridConfig: IAviCollectionDataGridConfig;

    /**
     * WAF CRS Collection instance.
     */
    public readonly wafCrsCollection: WafCrsCollection;

    /**
     * CRS file data to be uploaded.
     */
    public file: string | undefined;

    /**
     * CRS file name shown in the file selector.
     */
    public fileName: string | undefined;

    /**
     * Errors seen during the upload process.
     */
    public error: string;

    /**
     * Loading state of the upload component. If upload is in progress, show a spinner for the
     * upload button to prevent triggering multiple uploads.
     */
    public loadingState: LoadingState = LoadingState.DEFAULT;

    /**
     * CRS information for new available CRS releases.
     */
    public newCrsInfo: ICRSDetails | null = null;

    constructor(
        private readonly l10nService: L10nService,
        private readonly latestWafCrsVersionService: LatestWafCrsVersionService,
        @Inject(WafCrsCollection)
        WafCrsCollection: TWafCrsCollection,
        private readonly notificationService: NotificationService,
    ) {
        l10nService.registerSourceBundles(dictionary);

        this.wafCrsCollection = new WafCrsCollection({
            params: {
                fields: 'name,release_date,description,tenant_ref',
            },
            sortBy: '-version',
        });
    }

    /** @override */
    public ngOnInit(): void {
        this.wafCrsGridConfig = {
            id: 'waf-crs-list-page',
            collection: this.wafCrsCollection,
            fields: [
                {
                    id: 'name',
                    label: this.l10nService.getMessage(globalL10nKeys.nameLabel),
                    transform: (row: WafCrs) => row.getName(),
                },
                {
                    id: 'date',
                    label: this.l10nService.getMessage(l10nKeys.columnTitleReleaseDate),
                    transform: (row: WafCrs) => row.getConfig().release_date,
                },
            ],
            layout: {
                placeholderMessage: this.l10nService.getMessage(globalL10nKeys.noItemsFoundLabel),
                hideEdit: true,
            },
        };

        this.latestWafCrsVersionService.getLatestVersions().then(this.handleCrsUpdate);
    }

    /** @override */
    public ngAfterViewInit(): void {
        this.wafCrsGridConfig = {
            ...this.wafCrsGridConfig,
            expandedContentTemplateRef: this.expandedContentTemplateRef,
        };
    }

    /** @override */
    public ngOnDestroy(): void {
        this.wafCrsCollection.destroy();
    }

    /**
     * Handle CRS file selection change.
     */
    public handleFileChange(): void {
        this.error = '';
    }

    /**
     * Handler for uploading a CRS file. POSTs the CRS data and retrieves the latest CRS info to
     * verify that no new releases are available.
     */
    public async handleFileUpload(): Promise<void> {
        this.error = '';
        this.loadingState = LoadingState.LOADING;

        try {
            await this.wafCrsCollection.uploadWafCrs(this.file);

            this.notificationService.addSimpleNotification(
                this.l10nService.getMessage(globalL10nKeys.fileUploadedSuccessfullyMessage),
                this.fileName,
            );

            // Reset file and fileName after a successful upload.
            this.file = undefined;
            this.fileName = undefined;

            this.wafCrsCollection.load();
            this.latestWafCrsVersionService.getLatestVersions().then(this.handleCrsUpdate);
        } catch (error) {
            this.error = error;
        } finally {
            this.loadingState = LoadingState.DEFAULT;
        }
    }

    /**
     * Check if the latest CRS version on portal matches the latest CRS version of the controller.
     * Modify newCrsInfo if portal version is more recent than controller version.
     */
    private handleCrsUpdate = (
        [latestPortalCrsInfo, latestControllerCrsInfo]: [ICRSDetails, ICRSDetails],
    ): void => {
        this.newCrsInfo = null;

        if (latestPortalCrsInfo) {
            const portalCrsReleaseDate = new Date(latestPortalCrsInfo.release_date);
            const controllerCrsReleaseDate = new Date(latestControllerCrsInfo.release_date);

            if (portalCrsReleaseDate > controllerCrsReleaseDate) {
                this.newCrsInfo = latestPortalCrsInfo;
            }
        }
    };
}

@NgModule({
    declarations: [
        WafCrsListPageComponent,
    ],
    imports: [
        DataGridModule,
        SharedModule,
        AviCardModule,
        VIPModule.forChild(),
    ],
    schemas: [
        CUSTOM_ELEMENTS_SCHEMA,
        NO_ERRORS_SCHEMA,
    ],
})
/* eslint-disable-next-line no-unused-vars */
class WafCrsListPageModule {}
